import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Textarea } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import {
  Message,
  useSettings,
  Chat,
  Attachment,
  EditProposal,
} from "../../../contexts/SettingsContext";
import AttachmentPreview from "./Composer/AttachmentPreview";
import ComposerActions from "./Composer/ComposerActions";
import EmptyState from "./EmptyState";
import MessagesList from "./MessagesList";
import { usePageStyles } from "./style";
import MessageDialog from "../../UI/MessageDialog";
import { createAPIService } from "../../../utils/ApiService";
import { generateId } from "../../../utils/Helpers";
import { buildAttachment } from "../../../utils/RangeUtils";
import { parseAiResponse } from "../../../utils/EditProposals";

const MainPage: React.FC = () => {
  const styles = usePageStyles();
  const { t } = useTranslation();
  const { settings, updateSettings } = useSettings();

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>(() => {
    // Prefer current chat messages if multi-chat exists
    if (settings.chats && settings.currentChatId) {
      const current = settings.chats.find((c) => c.id === settings.currentChatId);
      return current?.messages || [];
    }
    return settings.messages;
  });
  const [attachments, setAttachments] = useState<(string | Attachment)[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    message: "",
  });

  React.useEffect(() => {
    document.title = "Excel AI Addin";
  }, []);

  const showAlert = (alertKey: keyof typeof alertKeys) => {
    setAlertConfig({
      title: t(`alerts.${alertKey}.title`),
      message: t(`alerts.${alertKey}.message`),
    });
    setAlertOpen(true);
  };

  const alertKeys = {
    officeNotAvailable: "officeNotAvailable",
    wrongHost: "wrongHost",
    readError: "readError",
  } as const;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (shouldFocusInput) {
      textareaRef.current?.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput]);

  useEffect(() => {
    // Initial focus on component mount
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // When settings change, load messages from current chat or fallback
    if (settings.chats && settings.currentChatId) {
      const current = settings.chats.find((c) => c.id === settings.currentChatId);
      setMessages(current?.messages || []);
    } else {
      setMessages(settings.messages);
    }
  }, [settings.messages, settings.chats, settings.currentChatId]);

  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].role === "assistant") {
      // Persist into current chat
      if (settings.chats && settings.currentChatId) {
        const updatedChats = settings.chats.map((c) =>
          c.id === settings.currentChatId ? { ...c, messages: messages, updatedAt: Date.now() } : c
        );
        updateSettings({ chats: updatedChats });
      } else {
        // Fallback to legacy single-chat storage
        updateSettings({ messages });
      }
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Helper function to update message status
  const updateMessageStatus = useCallback((messageId: string, updates: Partial<Message>) => {
    setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, ...updates } : msg)));
  }, []);

  const sendMessage = async (retryMessageId?: string) => {
    const isRetrying = !!retryMessageId;

    if (!isRetrying) {
      // NEW MESSAGE
      if (!input.trim() && attachments.length === 0) return;

      const userMessage: Message = {
        role: "user",
        content: input || t("chat.userFallbackMessage"),
        attachments: attachments.length ? [...attachments] : undefined,
        id: generateId(),
        status: "sending",
        timestamp: Date.now(),
      };

      // If there is no active chat, create one and name it from the first message
      let chatId = settings.currentChatId;
      let wasNewChat = false;
      if (!chatId) {
        const now = Date.now();
        const name =
          (input || t("chat.userFallbackMessage")).slice(0, 60).trim() ||
          t("chat.newChat", "New chat");
        chatId = `${now}`;
        wasNewChat = true;
        const newChat: Chat = {
          id: chatId,
          name,
          createdAt: now,
          updatedAt: now,
          messages: [userMessage],
        };
        updateSettings({ chats: [...(settings.chats || []), newChat], currentChatId: chatId });
      }

      // Append the message to state and persist immediately for stability across rerenders
      if (wasNewChat) {
        setMessages([userMessage]);
      } else {
        const next = [...messages, userMessage];
        setMessages(next);
        if (settings.chats && settings.currentChatId) {
          const updatedChats = settings.chats.map((c) =>
            c.id === settings.currentChatId ? { ...c, messages: next, updatedAt: Date.now() } : c
          );
          updateSettings({ chats: updatedChats });
        } else {
          // Legacy single-chat fallback
          updateSettings({ messages: next });
        }
      }
      setInput("");
      setAttachments([]);
      setRetryingMessageId(null);

      // Now send the API request
      await sendApiRequest(
        userMessage.id!,
        userMessage.content,
        userMessage.attachments || [],
        false
      );
    } else {
      // RETRY MESSAGE
      // Find the message to retry
      const messageToRetry = messages.find((msg) => msg.id === retryMessageId);
      if (!messageToRetry) return;

      // Update the message status to 'sending' again
      updateMessageStatus(retryMessageId, {
        status: "sending",
        error: undefined,
      });
      setRetryingMessageId(retryMessageId);

      // Send API request for retry
      await sendApiRequest(
        retryMessageId,
        messageToRetry.content,
        messageToRetry.attachments || [],
        true
      );
    }
  };

  const sendApiRequest = async (
    messageId: string,
    content: string,
    messageAttachments: (string | Attachment)[],
    isRetry: boolean
  ) => {
    setIsTyping(true);

    // Create API service instance
    const apiService = createAPIService(settings.selectedService, settings.apiKey, settings.apiUrl);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      // Prepare history
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);
      const history = messageIndex !== -1 ? messages.slice(0, messageIndex) : messages;

      const response = await apiService.sendChatCompletion(content, messageAttachments, history, {
        temperature: 0.7,
        maxTokens: 2000,
      });

      // Update user message status to 'sent'
      updateMessageStatus(messageId, {
        status: "sent",
        error: undefined,
      });

      // Add or replace assistant response
      const { text: displayText, proposals } = parseAiResponse(response);

      const assistantMessage: Message = {
        role: "assistant",
        content: displayText,
        id: generateId(),
        status: "sent",
        timestamp: Date.now(),
        proposals,
      };

      setMessages((prev) => {
        const newMessages = [...prev];
        const userMessageIndex = newMessages.findIndex((msg) => msg.id === messageId);

        if (userMessageIndex === -1) return newMessages;

        const nextMessage = newMessages[userMessageIndex + 1];
        if (nextMessage?.role === "assistant") {
          newMessages[userMessageIndex + 1] = assistantMessage;
        } else {
          newMessages.splice(userMessageIndex + 1, 0, assistantMessage);
        }

        // Also persist to active chat immediately
        if (settings.chats && settings.currentChatId) {
          const updatedChats = settings.chats.map((c) =>
            c.id === settings.currentChatId
              ? { ...c, messages: newMessages, updatedAt: Date.now() }
              : c
          );
          updateSettings({ chats: updatedChats });
        }

        return newMessages;
      });
      setRetryingMessageId(null);

      setShouldFocusInput(true);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Request cancelled");

        if (isRetry) {
          updateMessageStatus(messageId, {
            status: "error",
            error: "Cancelled",
          });
        } else {
          setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        }
        return;
      }

      console.error("API request failed:", error);

      updateMessageStatus(messageId, {
        status: "error",
        error: error instanceof Error ? error.message : "Unknown error",
      });

      if (isRetry) {
        setMessages((prev) => {
          const newMessages = [...prev];
          const userMessageIndex = newMessages.findIndex((msg) => msg.id === messageId);

          if (userMessageIndex !== -1) {
            const nextMessage = newMessages[userMessageIndex + 1];
            if (nextMessage?.role === "assistant") {
              newMessages.splice(userMessageIndex + 1, 1);
            }
          }

          return newMessages;
        });
      }
    } finally {
      setIsTyping(false);
      setAbortController(null);
    }
  };

  const retryMessage = (messageId: string) => {
    sendMessage(messageId);
  };

  const updateMessageProposals = (messageId: string, proposals: EditProposal[]) => {
    setMessages((prev) => {
      const newMessages = prev.map((msg) => (msg.id === messageId ? { ...msg, proposals } : msg));

      if (settings.chats && settings.currentChatId) {
        const updatedChats = settings.chats.map((c) =>
          c.id === settings.currentChatId
            ? { ...c, messages: newMessages, updatedAt: Date.now() }
            : c
        );
        updateSettings({ chats: updatedChats });
      }

      return newMessages;
    });
  };

  const stopGeneration = () => {
    if (abortController) {
      abortController.abort();
      setIsTyping(false);
      setRetryingMessageId(null);
    }
  };

  const attachSelectedRows = async () => {
    if (typeof Office === "undefined") {
      showAlert("officeNotAvailable");
      return;
    }

    if (Office.context.host !== Office.HostType.Excel) {
      showAlert("wrongHost");
      return;
    }

    try {
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load(["values", "address", "rowIndex", "columnIndex", "columnCount"]);

        await context.sync();

        const values = range.values;

        if (!values || values.length === 0) {
          return;
        }

        const attachment = buildAttachment(
          values,
          range.address,
          range.rowIndex,
          range.columnIndex,
          range.columnCount
        );

        setAttachments((prev) => [...prev, attachment]);
      });
    } catch (error) {
      console.error("Failed to read selected Excel rows:", error);
      showAlert("readError");
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <MessageDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        okText={t("buttons.ok")}
      />

      <div className={styles.root}>
        {!messages.length ? (
          <EmptyState styles={styles} />
        ) : (
          <MessagesList
            messages={messages}
            isTyping={isTyping}
            styles={styles}
            messagesEndRef={messagesEndRef}
            onRetry={retryMessage}
            retryingMessageId={retryingMessageId}
            onProposalsChange={updateMessageProposals}
          />
        )}

        <div className={styles.composerWrapper}>
          <div className={styles.composer}>
            {attachments.length > 0 && (
              <AttachmentPreview
                attachments={attachments}
                styles={styles}
                onRemove={(i) => setAttachments((p) => p.filter((_, idx) => idx !== i))}
                onClear={() => setAttachments([])}
              />
            )}

            <Textarea
              ref={textareaRef}
              className={styles.textarea}
              placeholder={t("chat.textareaPlaceholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              disabled={isTyping}
              resize="none"
            />

            <ComposerActions
              styles={styles}
              isTyping={isTyping}
              attachments={attachments}
              onAttach={attachSelectedRows}
              onSend={() => sendMessage()}
              onStop={stopGeneration}
              canSend={!!input.trim() || attachments.length > 0}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MainPage;
